"use client";

import { createPortal } from "react-dom";
import { Button } from "@/components/ui/button";
import { Plus, GripVertical } from "lucide-react";
import { SortableTierItem, TierItem } from "@/components/sortable-tier-item";
import { TierItemContent } from "@/components/tier-item-content";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  MeasuringStrategy,
  DragOverlay,
} from "@dnd-kit/core";
import { restrictToWindowEdges } from "@dnd-kit/modifiers";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";

interface TiersSectionProps {
  tiers: TierItem[];
  onLabelChange: (index: number, label: string) => void;
  openColorModal: (index: number) => void;
  removeTier: (index: number) => void;
  addTier: () => void;
  onDragStart: (event: DragStartEvent) => void;
  onDragEnd: (event: DragEndEvent) => void;
  activeId: string | null;
}

export function TiersSection({
  tiers,
  onLabelChange,
  openColorModal,
  removeTier,
  addTier,
  onDragStart,
  onDragEnd,
  activeId,
}: TiersSectionProps) {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium">Tiers</h3>
        <Button type="button" variant="outline" size="sm" onClick={addTier}>
          <Plus className="w-4 h-4 mr-2" />
          Add Tier
        </Button>
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        measuring={{
          droppable: {
            strategy: MeasuringStrategy.Always,
          },
        }}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
      >
        <SortableContext
          items={tiers.map((tier) => tier.id)}
          strategy={verticalListSortingStrategy}
        >
          <div className="space-y-2">
            {tiers.map((tier, index) => (
              <SortableTierItem
                key={tier.id}
                id={tier.id}
                index={index}
                tier={tier}
                onLabelChange={onLabelChange}
                openColorModal={openColorModal}
                removeTier={removeTier}
                tiersLength={tiers.length}
              />
            ))}
          </div>
        </SortableContext>
        {typeof document !== "undefined" &&
          createPortal(
            <DragOverlay modifiers={[restrictToWindowEdges]}>
              {activeId ? (() => {
                const activeTier = tiers.find((tier) => tier.id === activeId);
                const activeIndex = tiers.findIndex((tier) => tier.id === activeId);
                if (!activeTier || activeIndex === -1) return null;

                return (
                  <div className="flex gap-4 items-start p-4 border rounded-lg bg-card opacity-90 cursor-grabbing">
                    <div className="flex gap-2 items-center w-full">
                      <button
                        type="button"
                        className="cursor-grabbing text-muted-foreground"
                      >
                        <GripVertical className="w-5 h-5" />
                      </button>

                      <TierItemContent
                        tier={activeTier}
                        index={activeIndex}
                        tiersLength={tiers.length}
                        readOnly
                      />
                    </div>
                  </div>
                );
              })() : null}
            </DragOverlay>,
            document.body
          )}
      </DndContext>
    </div>
  );
}
